import { AfterViewInit, OnDestroy, OnInit, ViewChild, Directive } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {merge, Subject} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';
import {TableUtils} from '../utils/table.utils';
import {PagedResponse} from '../models/paged-response';
import {SelectionModel} from '@angular/cdk/collections';
import {NbToastrService} from '@nebular/theme';

@Directive()
export class DataTableComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('masterToggleCheckbox') masterToggleCheckbox: MatCheckbox;

    data: any[];
    dataLength = 0;
    isLoading = false;

    activeSort;
    activeSortDirection;
    activePage = 0;
    activeLength = 50;
    pageSizeOptions = [10, 25, 50, 100];

    queryParams: ParamMap;
    displayedColumns: string[];

    selection = new SelectionModel<string>(true, []);
    isAllFromPageSelected = false;

    protected unsubscribe$ = new Subject<void>();

    constructor(
        protected toastr: NbToastrService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected getData: (params) => Promise<PagedResponse<any>>
    ) {
    }

    ngOnInit() {
        this.route.queryParamMap
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(params => {
                this.queryParams = params;
                this.handleParamsChanged();
            });
    }

    ngAfterViewInit() {
        merge(
            this.paginator.page,
            this.sort.sortChange,
        )
            .pipe(
                tap(async () => {
                    this.setPagingParams();
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    masterToggle(event) {
        this.isAllFromPageSelected
            ? this.selection.clear()
            : this.data.forEach(row => this.selection.select(row._id));
        this.isAllFromPageSelected = !this.isAllFromPageSelected;
    }

    toggleCheckbox(id) {
        this.selection.toggle(id);
        this.setMasterToggleState();
    }

    clearSelection() {
        this.selection.clear();
        this.masterToggleCheckbox.checked = false;
        this.masterToggleCheckbox.indeterminate = false;
        this.isAllFromPageSelected = false;
    }

    async setMasterToggleState() {
        if (!this.masterToggleCheckbox) return;
        const pageIds = this.data.map(user => user._id);
        let intersect = false;
        let containsAllFromPage = true;
        for (const id of pageIds) {
            if (this.selection.isSelected(id)) {
                intersect = true;
            } else {
                containsAllFromPage = false;
            }
        }
        this.masterToggleCheckbox.checked = containsAllFromPage;
        this.masterToggleCheckbox.indeterminate = intersect && !containsAllFromPage;
        this.isAllFromPageSelected = containsAllFromPage;
    }

    async setPagingParams() {
        const queryParams = {
            start: this.paginator.pageIndex,
            length: this.paginator.pageSize,
            sortOrder: this.sort.direction,
            column: this.sort.active,
        };
        if (!queryParams.sortOrder) queryParams.column = "";
        await this.setQueryParams(queryParams);
    }

    async setQueryParams(queryParams) {
        await this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: 'merge'
        });
    }

    async handleParamsChanged() {
        const params = this.queryParams;
        const queryParams = TableUtils.parseParams(params, this.activePage, this.activeLength, this.activeSort, this.activeSortDirection);

        this.activePage = queryParams.start;
        this.activeLength = queryParams.length;
        TableUtils.setSortColumn(this.sort, this.activeSort);

        this.loadData(queryParams);
    }

    async loadData(params) {
        try {
            this.isLoading = true;
            const data = await this.getData(params);
            this.data = data.data;
            this.dataLength = data.total;
            this.setMasterToggleState();
        } catch (err) {
            console.error(err);
            this.toastr.danger(this.toUserMessage(err), 'Eroare');
        } finally {
            this.isLoading = false;
        }
    }

    private toUserMessage(err: any): string {
        // status 0 => network/connection failure, no HTTP response (raw message is "0 Unknown Error")
        if (err?.status === 0) {
            return navigator.onLine
                ? 'Nu am putut contacta serverul. Încearcă din nou în câteva momente.'
                : 'Nu ai conexiune la internet. Verifică conexiunea și încearcă din nou.';
        }
        return err?.msg || err?.error?.error?.msg || 'A apărut o eroare. Încearcă din nou.';
    }
}
